import companyCustomFormsApi from '@src/@data/api/companyCustomForms';
import { useInternationalization } from '@src/@data/store/InternationalizationProvider';
import {
  CustomForm as CustomFormType,
  CustomFormField,
  CustomFormFieldPlaceholder,
  CustomFormFieldTextInputType,
  CustomFormFieldType,
  CustomFormWithValues,
} from '@src/@data/types/customForms';
import { PromoCode } from '@src/@data/types/promoCodes';
import { toastOnError } from '@src/@data/utility/errors';
import { useMessages } from '@src/@data/utility/hooks/useMessages';
import { UILoader } from '@src/components/atoms/ui-loader';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { Label } from 'reactstrap';
import { CustomFormCheckboxField } from './CustomFormCheckboxField';
import { CustomFormSelectField } from './CustomFormSelectField';
import { CustomFormTextField } from './CustomFormTextField';
import { CompanyClient } from '@src/@data/types/clients';

export interface CustomFormProps {
  companyId: string;
  customFormId?: string;
  client: CompanyClient | undefined;
  customFormValues?: CustomFormWithValues;
  setCustomFormValues: (data: CustomFormWithValues | undefined) => void;
}

const getDefaultFieldValue = (
  field: CustomFormField,
  user: PromoCode['client']
): string | boolean | number => {
  switch (field.type) {
    case CustomFormFieldType.Text:
      if (field.inputType === CustomFormFieldTextInputType.Number) {
        return 0;
      }
      switch (field.placeholder) {
        case CustomFormFieldPlaceholder.FirstName:
          return user?.firstName ?? '';
        case CustomFormFieldPlaceholder.LastName:
          return user?.lastName ?? '';
        case CustomFormFieldPlaceholder.Email:
          return user?.email ?? '';
        case CustomFormFieldPlaceholder.Gender:
          // there is no user gender data
          return '';
        case CustomFormFieldPlaceholder.PhoneNumber:
          return user ? `+${user.phoneNumber}` : '';
        default:
          return '';
      }
    case CustomFormFieldType.Select:
      return '';
    case CustomFormFieldType.Checkbox:
      return field.placeholder === CustomFormFieldPlaceholder.True;
  }
};

export const CustomForm: React.FC<CustomFormProps> = ({
  customFormId,
  companyId,
  client,
  customFormValues,
  setCustomFormValues,
}) => {
  const { messages } = useMessages();
  const { language } = useInternationalization();
  const [customForm, setCustomForm] = useState<CustomFormType>();
  const [isCustomFormLoading, setIsCustomFormLoading] = useState(false);

  const changeFieldValue = useCallback(
    (id: string, value: string | number | boolean) => {
      setCustomFormValues(
        customFormValues
          ? {
              blocks: customFormValues.blocks.map((block) => ({
                ...block,
                fields: block.fields.map((field) => ({
                  ...field,
                  value: field.id === id ? value : field.value,
                })),
              })),
            }
          : customFormValues
      );
    },
    [customFormValues, setCustomFormValues]
  );

  useEffect(() => {
    if (!customFormId) {
      return;
    }
    setIsCustomFormLoading(true);
    companyCustomFormsApi
      .getCustomForm(companyId, customFormId)
      .then((res) => {
        const customForm = res?.translations.find(
          (translation) => translation.language === language
        )?.customForm;
        setCustomForm(customForm);
        setCustomFormValues(
          customForm
            ? {
                blocks: customForm.blocks.map((block) => ({
                  id: block.id,
                  name: block.name,
                  fields: block.fields.map((field) => ({
                    id: field.id,
                    name: field.name,
                    value: getDefaultFieldValue(field, client),
                  })),
                })),
              }
            : undefined
        );
      })
      .catch(toastOnError)
      .finally(() => setIsCustomFormLoading(false));
  }, [companyId, customFormId, language, client, setCustomFormValues]);

  if (!customFormId) return null;

  return (
    <UILoader blocking={isCustomFormLoading}>
      <Label className="text-secondary mb-2">
        {messages('reservation_checkout_additional_info')}
      </Label>
      {customForm?.blocks.map((block) => (
        <>
          <div className="border p-2 rounded position-relative mb-2">
            <Label
              className="position-absolute top-0 text-secondary bg-white px-50 ml-n50"
              style={{ transform: 'translateY(-50%)' }}
            >
              {block.name}
            </Label>
            {block.fields.map((field) => {
              const value = customFormValues?.blocks
                .find((b) => b.id === block.id)
                ?.fields.find((f) => f.id === field.id)?.value;
              return (
                <Fragment key={field.id}>
                  {field.type === CustomFormFieldType.Text && (
                    <CustomFormTextField
                      field={field}
                      inputName={field.id}
                      value={value as string | number | undefined}
                      onChangeField={(val) => changeFieldValue(field.id, val)}
                    />
                  )}
                  {field.type === CustomFormFieldType.Checkbox && (
                    <CustomFormCheckboxField
                      field={field}
                      inputName={field.id}
                      value={value as boolean | undefined}
                      onChangeField={(val) => changeFieldValue(field.id, val)}
                    />
                  )}
                  {field.type === CustomFormFieldType.Select && (
                    <CustomFormSelectField
                      field={field}
                      inputName={field.id}
                      value={value as string | undefined}
                      onChangeField={(val) => changeFieldValue(field.id, val)}
                    />
                  )}
                </Fragment>
              );
            })}
          </div>
        </>
      ))}
    </UILoader>
  );
};
